import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  Alert,
} from '@mui/material';
import { PlayArrow, Timer, People } from '@mui/icons-material';
import { api } from '@/services/api';
import { useSessionStore, useUIStore } from '@/stores';

const scenarios = [
  {
    id: 'inbox-crisis-v1',
    title: 'The Inbox Crisis',
    description: 'Manage a critical client situation through email and Slack communication',
    duration: 900, // 15 minutes
    difficulty: 'Intermediate',
    capabilities: ['Communication', 'Decision Making', 'Leadership'],
    npcs: 3,
  },
];

const EDGEHUB_URL = import.meta.env.VITE_EDGEHUB_URL || 'http://localhost:3000';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { setLoading, setError, isEdgeHubSession, edgeHubUserId, edgeHubUserName, scenarioFromUrl, setScenarioFromUrl } = useSessionStore();
  const { addNotification } = useUIStore();

  const [autoLaunching, setAutoLaunching] = useState(false);
  const [autoLaunchError, setAutoLaunchError] = useState<string | null>(null);

  // Auto-launch when coming from EdgeHub with a scenario
  useEffect(() => {
    if (isEdgeHubSession && scenarioFromUrl && !autoLaunching) {
      setAutoLaunching(true);
      const scenarioId = scenarioFromUrl;
      setScenarioFromUrl(null); // Prevent re-trigger
      (async () => {
        try {
          setLoading(true);
          const session = await api.createSession({
            userId: edgeHubUserId || '',
            scenarioId,
          });
          addNotification({ type: 'success', message: 'Simulation session created!' });
          navigate(`/simulation/${session.id}`);
        } catch (error: any) {
          console.error('Error auto-creating session:', error);
          const message = error?.response?.data || error?.message || 'Failed to create simulation session';
          setAutoLaunchError(typeof message === 'string' ? message : 'Failed to create simulation session');
          setAutoLaunching(false);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [isEdgeHubSession, scenarioFromUrl]);

  // Show loading state while auto-launching into a scenario
  if (autoLaunching || scenarioFromUrl) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          EdgePlay
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
          Launching simulation...
        </Typography>
      </Container>
    );
  }

  // Show error if auto-launch failed
  if (autoLaunchError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          EdgePlay
        </Typography>
        <Alert severity="error" sx={{ mt: 3, mb: 3 }}>
          {autoLaunchError}
        </Alert>
        <Button
          variant="contained"
          onClick={() => { window.location.href = `${EDGEHUB_URL}`; }}
        >
          Return to EdgeHub
        </Button>
      </Container>
    );
  }

  // If no EdgeHub context, show auth-required message
  if (!isEdgeHubSession) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          EdgePlay
        </Typography>
        <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
          Please launch EdgePlay from EdgeHub to start a simulation.
        </Alert>
        <Button
          variant="contained"
          onClick={() => { window.location.href = `${EDGEHUB_URL}/on-demand`; }}
        >
          Go to EdgeHub
        </Button>
      </Container>
    );
  }

  const handleStartSimulation = async (scenarioId: string) => {
    try {
      setLoading(true);
      const session = await api.createSession({
        userId: edgeHubUserId || '',
        scenarioId,
      });
      addNotification({ type: 'success', message: 'Simulation session created!' });
      navigate(`/simulation/${session.id}`);
    } catch (error) {
      console.error('Error creating session:', error);
      setError('Failed to create simulation session');
      addNotification({ type: 'error', message: 'Failed to create simulation session' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h2" component="h1" gutterBottom color="primary">
          EdgePlay
        </Typography>
        <Typography variant="h5" component="h2" color="text.secondary" mb={2}>
          Modular Business Simulation Platform
        </Typography>
        {isEdgeHubSession && edgeHubUserName && (
          <Typography variant="subtitle1" color="primary" mb={1}>
            Welcome, {edgeHubUserName}
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" maxWidth={600} mx="auto">
          Experience realistic workplace scenarios through AI-driven simulations.
          Make decisions, communicate with NPCs, and develop critical business skills.
        </Typography>
      </Box>

      <Typography variant="h4" component="h2" gutterBottom mb={3}>
        Available Scenarios
      </Typography>

      <Grid container spacing={3}>
        {scenarios.map((scenario) => (
          <Grid item xs={12} md={6} lg={4} key={scenario.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  {scenario.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {scenario.description}
                </Typography>

                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Timer fontSize="small" color="primary" />
                    <Typography variant="caption">
                      {Math.floor(scenario.duration / 60)} min
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <People fontSize="small" color="primary" />
                    <Typography variant="caption">
                      {scenario.npcs} NPCs
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Difficulty: {scenario.difficulty}
                </Typography>

                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Skills:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {scenario.capabilities.map((skill) => (
                      <Typography 
                        key={skill}
                        variant="caption" 
                        sx={{ 
                          px: 1, 
                          py: 0.5, 
                          bgcolor: 'primary.50', 
                          color: 'primary.700',
                          borderRadius: 1,
                          fontSize: '0.75rem'
                        }}
                      >
                        {skill}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </CardContent>

              <Box p={2} pt={0}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PlayArrow />}
                  onClick={() => handleStartSimulation(scenario.id)}
                  sx={{
                    py: 1.5,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  Start Simulation
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

    </Container>
  );
};

export default Dashboard;